import React, { useState, useEffect, useMemo, useCallback } from "react";
import Dropzone from "react-dropzone";
import styled from "styled-components";
import Edit from "../../icons/edit";
import Delete from "../../icons/delete";
import { Button, Modal, ModalDialog } from "react-bootstrap";
import { Alert } from "react-bootstrap-3";
import { arrayMove, SortableContainer, SortableElement } from "react-sortable-hoc";
import ImageManagerModalEdit from "./ImageManagerModalEdit";
import { defineMessages, FormattedMessage } from "react-intl";
import globalMessages from "../../intl/globalMessages";
import { v4 as uuidv4 } from "uuid";

const messages = defineMessages({
  ...globalMessages,
  dropHere: {
    id: "app.imageManager.dropHere",
    description: "Drop files here...",
    defaultMessage: "Drop files here...",
  },
  deleteSelected: {
    id: "app.imageManager.deleteSelected",
    description: "Delete Selected",
    defaultMessage: "Delete Selected",
  },
  editImage: {
    id: "app.imageManager.editImage",
    description: "Edit Image",
    defaultMessage: "Edit Image",
  },
  deleteImage: {
    id: "app.imageManager.deleteImage",
    description: "Delete Image?",
    defaultMessage: "Delete Image?",
  },
  deleteImages: {
    id: "app.imageManager.deleteImages",
    description: "Delete Images?",
    defaultMessage: "Delete Images?",
  },
  name: {
    id: "app.imageManager.name",
    description: "Name",
    defaultMessage: "Name",
  },
  title: {
    id: "app.imageManager.title",
    description: "Title",
    defaultMessage: "Title",
  },
  description: {
    id: "app.imageManager.description",
    description: "Description",
    defaultMessage: "Description",
  },
  cancel: {
    id: "app.imageManager.cancel",
    description: "Cancel",
    defaultMessage: "Cancel",
  },
  delete: {
    id: "app.imageManager.delete",
    description: "Delete",
    defaultMessage: "Delete",
  },
  saveChanges: {
    id: "app.imageManager.saveChanges",
    description: "Save Changes",
    defaultMessage: "Save Changes",
  },
  alreadyExists: {
    id: "app.imageManager.alreadyExists",
    description: "There is an image with the same name",
    defaultMessage: "There is an image with the same name",
  },
  containsSpecials: {
    id: "app.imageManager.containsSpecials",
    description: "Image name contains special characters",
    defaultMessage: "Image name contains special characters",
  },
  overSizeLimit: {
    id: "app.imageManager.overSizeLimit",
    description: "The image exceeds maximum allowed size",
    defaultMessage: "The image exceeds maximum allowed size",
  },
  belowMinWidth: {
    id: "app.imageManager.belowMinWidth",
    description: "The image does not have the minimum size required",
    defaultMessage: "The image does not have the minimum size required",
  },
  overFilesLimit: {
    id: "app.imageManager.overFilesLimit",
    description: "Maximum number of images reached",
    defaultMessage: "Maximum number of images reached",
  },
  singleFileLimit: {
    id: "app.imageManager.singleFileLimit",
    description: "singleFileLimit",
    defaultMessage: "singleFileLimit",
  },
});

const Thumb = styled.div`
  display: inline-flex;
  border-radius: 2px;
  border: 1px solid #eaeaea;
  margin-bottom: 15px;
  margin-right: 5px;
  margin-left: 5px;
  width: 120px;
  height: 120px;
  box-sizing: border-box;
  border: ${props => (props.selected ? `3px solid #32f35c` : `inherit`)};
  position: relative;
  cursor: pointer;
`;

const Overlay = styled.div`
  opacity: 0.9;
  fill: white;
  color: white;
  position: absolute;
  right: 0;
  display: none;
  z-index: 1;
  border-radius: 50px;
  width: ${props => (props.allowImageEdit ? `76px` : `40px`)};
  height: 40px;
  background-color: #656565;
  justify-content: space-evenly;
  align-items: center;

  ${Thumb}:hover & {
    display: flex;
  }

  cursor: default;
`;

const ThumbInner = styled.div`
  display: flex;
  min-width: 0;
  overflow: hidden;
  background-color: ${props =>
    props.addBackgroundToPreview ? `rgba(0, 0, 0, 0.1)` : `transparent`};
  padding: ${props => (props.addBackgroundToPreview ? `8px` : `0px`)};
`;

const Img = styled.img`
  display: block;
  height: 100%;
  width: 100%;
  object-fit: cover;
`;
const StyledEdit = styled(Edit)`
  height: 24px;
  width: 24px;
  cursor: pointer;
`;
const StyledDelete = styled(Delete)`
  height: 24px;
  width: 24px;
  cursor: pointer;
`;

const DropzoneContainer = styled.div`
  width: auto;
  height: 100px;
  border: 2px dashed #dae6e8;
  background-color: ${({ theme }) => theme.greyBackground};
  font-size: 21px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: 25px;
`;

const Container = styled.div`
  padding: 25px;
  border: 1px solid #e5e5e5;
  margin-bottom: 25px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledDropzone = styled(Dropzone)`
  margin-bottom: 25px;
`;

const DnDContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
`;

const WarningMessage = styled.label`
  color: red !important;
`;

const HelpTextMessage = styled.label`
  display: inline-block;
  max-width: 100%;
  margin-bottom: 5px;
  font-weight: 600;
  white-space: pre-line;
`;

export function customCloneFiles(filesArray) {
  return (
    filesArray &&
    filesArray.map(file => {
      const lFile = new File([file.slice()], file.name, { type: file.type });
      lFile.preview = file.preview;
      lFile.url = file.url;
      lFile.translations = file.translations ? JSON.parse(JSON.stringify(file.translations)) : {};
      lFile.modifiedName = file.modifiedName;
      lFile.id = file.id;

      return lFile;
    })
  );
}

const error = (stateParam, msg) => {
  return (
    stateParam.length > 0 && (
      <Alert bsStyle="danger">
        <strong>
          {msg}: {stateParam.map((it, idx) => `${it}${idx + 1 !== stateParam.length ? `, ` : ``}`)}
        </strong>
      </Alert>
    )
  );
};

const SortableItem = SortableElement(({ value }) => <span>{value}</span>);

const SortableList = SortableContainer(({ items }) => {
  return (
    <DnDContainer>
      {items &&
        items.map((value, index) => (
          <SortableItem key={`item-${index}`} index={index} value={value} />
        ))}
    </DnDContainer>
  );
});

function dimensionsImageValid(file, { minWidth }) {
  return new Promise(resolve => {
    const path = URL.createObjectURL(file);
    const image = new Image();
    image.src = path;
    image.onload = function () {
      URL.revokeObjectURL(path);
      if (this.width < minWidth) {
        return resolve(false);
      }
      return resolve(true);
    };
  });
}

function ImageManager({
  onChange,
  formValues,
  field,
  locales,
  isDisabled = false,
  cleanFiles = true,
  config: {
    maxSizeMB = 5.5,
    maxFiles,
    minWidth = undefined,
    formats,
    addBackgroundToPreview = false,
    helpText,
  } = {},
  allowImageEdit,
  allowDeleteSelection,
  errors = {},
} = {}) {
  const [files, setFiles] = useState(formValues.multimedia || []);
  const [selected, setSelected] = useState([]);
  const [showModalEdit, setShowModalEdit] = useState("");
  const [showModalDelete, setShowModalDelete] = useState([]);
  const [alreadyExists, setAlreadyExists] = useState([]);
  const [containsSpecial, setContainsSpecial] = useState(errors.containsSpecial || []);
  const [overSizeLimit, setOverSizeLimit] = useState(errors.overSizeLimit || []);
  const [belowMinWidth, setBelowMinWidth] = useState(errors.belowMinWidth || []);
  const [tempFiles, setTempFiles] = useState(customCloneFiles(files));

  const multimedia = formValues && formValues.multimedia;
  // Comparable to componentDidUpdate
  useEffect(() => {
    setFiles(multimedia || []);
    setTempFiles(multimedia || []);
  }, [multimedia]);

  // Only needs to happen once - on unmount, thus []
  useEffect(() => {
    return () => {
      if (cleanFiles) {
        files && files.length > 0 && files.forEach(file => URL.revokeObjectURL(file.preview));
      }
    };
  }, [files, cleanFiles]);

  const showModalEditCallbackArray = useMemo(
    () => files.map(file => () => setShowModalEdit(file.id)),
    [files]
  );
  const showModalDeleteCallbackArray = useMemo(
    () => files.map(file => () => setShowModalDelete([file.id])),
    [files]
  );
  const callbackSetStateShowModalDelete = useCallback(() => {
    setShowModalDelete(selected);
  }, [selected]);

  const maxSizeBytes = useMemo(() => (maxSizeMB ? maxSizeMB * 1000000 : undefined), [maxSizeMB]);

  const isMaxFiles = useMemo(() => maxFiles && files.length >= maxFiles, [files, maxFiles]);

  const fieldName = field && field.fieldName;
  const onDrop = useCallback(
    async droppedFiles => {
      const lAlreadyExists = [];
      const lFiles = [];
      const lContainsSpecial = [];
      const lOverSizeLimit = [];
      const lBelowMinWidth = [];
      for (let index = 0; index < droppedFiles.length; index++) {
        const file = droppedFiles[index];
        const modifiedName = file.name.substr(0, file.name.lastIndexOf(".")).replace(/\s+/g, "-");
        if (file.size > maxSizeBytes) {
          lOverSizeLimit.push(modifiedName);
        } else if (minWidth && !(await dimensionsImageValid(file, { minWidth }))) {
          lBelowMinWidth.push(modifiedName);
        } else if (!RegExp(/^[a-zA-Z0-9-_]+$/).test(modifiedName)) {
          lContainsSpecial.push(modifiedName);
        } else if (
          files.some(
            stateFile => stateFile.modifiedName === modifiedName && stateFile.type === file.type
          )
        ) {
          lAlreadyExists.push(modifiedName);
        } else {
          lFiles.push(
            Object.assign(file, {
              preview: URL.createObjectURL(file),
              translations: {},
              modifiedName,
              id: uuidv4(),
              new: true,
            })
          );
        }
      }

      setFiles(files.concat(lFiles));
      setTempFiles(tempFiles.concat(customCloneFiles(lFiles)));
      setAlreadyExists(lAlreadyExists);
      setContainsSpecial(lContainsSpecial);
      setOverSizeLimit(lOverSizeLimit);
      setBelowMinWidth(lBelowMinWidth);
      onChange({
        value: files.concat(lFiles),
        field: fieldName,
        errors: {
          overSizeLimit: lOverSizeLimit,
          containsSpecial: lContainsSpecial,
          belowMinWidth: lBelowMinWidth,
        },
      });
    },
    [files, onChange, fieldName, tempFiles, maxSizeBytes, minWidth]
  );

  const handleSelect = useCallback(
    event => {
      const lSelected = selected.includes(event.target.name)
        ? selected.filter(name => name !== event.target.name)
        : selected.concat(event.target.name);
      setSelected(lSelected);
    },
    [selected]
  );

  const setStateTempFiles = useCallback(
    tmpFiles => {
      tmpFiles = customCloneFiles(tmpFiles);

      setFiles(tmpFiles);
      setShowModalEdit("");
      setTempFiles(tmpFiles);
      onChange({
        value: tmpFiles,
        field: fieldName,
      });
    },
    [fieldName, onChange]
  );

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }) => {
      const lFiles = arrayMove(files, oldIndex, newIndex);
      setFiles(lFiles);
      setTempFiles(customCloneFiles(lFiles));
      onChange({
        value: lFiles,
        field: fieldName,
      });
    },
    [files, onChange, fieldName]
  );

  const handleClose = useCallback(() => {
    setShowModalEdit("");
    setShowModalDelete([]);
    setTempFiles(customCloneFiles(files));
  }, [files]);

  const [lFiles, lSelected] = useMemo(() => {
    let lFiles = customCloneFiles(files);
    let lSelected = [...selected];
    const deleteFromStateCopy = showModalDelete => {
      const index = lFiles.findIndex(file => file.id === showModalDelete);
      lFiles = lFiles.filter((it, idx) => idx !== index);
      lSelected = lSelected.filter(it => it !== showModalDelete);
    };

    showModalDelete.forEach(clr => {
      deleteFromStateCopy(clr);
    });

    return [lFiles, lSelected];
  }, [files, selected, showModalDelete]);

  const handleDelete = useCallback(() => {
    setSelected(lSelected);
    setFiles(lFiles);
    setShowModalDelete([]);
    setTempFiles(customCloneFiles(lFiles));
    onChange({
      value: lFiles,
      field: fieldName,
    });
  }, [onChange, fieldName, lFiles, lSelected]);

  const Dialog = props => (
    <div className="witadmin-bs3">
      <ModalDialog {...props} />
    </div>
  );

  const modalDelete = useMemo(() => {
    return (
      <Modal
        dialogComponentClass={Dialog}
        show={showModalDelete.length > 0}
        onHide={handleClose}
        bsSize="large"
        centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {" "}
            {showModalDelete.length === 1 ? (
              <FormattedMessage {...messages.deleteImage} />
            ) : (
              <FormattedMessage {...messages.deleteImages} />
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          <Button onClick={handleClose}>
            {" "}
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button onClick={handleDelete}>
            <FormattedMessage {...messages.delete} />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }, [showModalDelete, handleDelete, handleClose]);

  const thumbs = useMemo(
    () =>
      files &&
      files.map((file, idx) => (
        <Thumb key={file.id} selected={selected.includes(file.id)} onClick={handleSelect}>
          {!isDisabled && (
            <>
              {allowImageEdit && (
                <Overlay allowImageEdit={allowImageEdit}>
                  <StyledEdit onClick={showModalEditCallbackArray[idx]} /> |
                </Overlay>
              )}
              {allowDeleteSelection && (
                <Overlay>
                  <StyledDelete onClick={showModalDeleteCallbackArray[idx]} />
                </Overlay>
              )}
            </>
            )}
            <ThumbInner addBackgroundToPreview={addBackgroundToPreview}>
              <Img src={file.preview} name={file.id} />
            </ThumbInner>
        </Thumb>
      )),
    [
      files,
      selected,
      handleSelect,
      allowImageEdit,
      isDisabled,
      showModalEditCallbackArray,
      showModalDeleteCallbackArray,
      addBackgroundToPreview
    ]
  );

  return (
    <section>
      <div className="flex flex-col">
        <HelpTextMessage className="text-warning">
          {helpText && <FormattedMessage {...helpText} />}
        </HelpTextMessage>
        {isMaxFiles && (
          <WarningMessage className="text-warning">
            {maxFiles === 1 ? (
              <FormattedMessage {...messages.singleFileLimit} values={{ formats }} />
            ) : (
              <FormattedMessage {...messages.overFilesLimit} values={{ maxFiles: maxFiles }} />
            )}
          </WarningMessage>
        )}
      </div>
      <Container>
        <StyledDropzone accept="image/*" onDrop={onDrop} disabled={isDisabled || isMaxFiles}>
          {({ getRootProps, getInputProps }) => (
            <DropzoneContainer {...getRootProps()}>
              <input {...getInputProps()} />
              <FormattedMessage {...messages.dropHere} />
            </DropzoneContainer>
          )}
        </StyledDropzone>
        {error(alreadyExists, <FormattedMessage {...messages.alreadyExists} />)}
        {error(containsSpecial, <FormattedMessage {...messages.containsSpecials} />)}
        {error(
          overSizeLimit,
          <FormattedMessage {...messages.overSizeLimit} values={{ maxSizeMB: `${maxSizeMB}MB` }} />
        )}
        {error(
          belowMinWidth,
          <FormattedMessage
            {...messages.belowMinWidth}
            values={{ minWidth: `${minWidth}px`, maxSizeMB: `${maxSizeMB}MB` }}
          />
        )}
        <span>
          <SortableList
            items={thumbs}
            onSortEnd={onSortEnd}
            axis="xy"
            helperClass="sortableHelper"
            distance={2}
          />
        </span>
        {!isDisabled && showModalEdit && (
          <ImageManagerModalEdit
            caller={showModalEdit}
            initialFiles={tempFiles}
            locales={locales}
            showModalEdit={showModalEdit}
            customCloneFiles={customCloneFiles}
            onModalChange={setStateTempFiles}
            handleClose={handleClose}
          />
        )}
        {showModalDelete.length > 0 && modalDelete}
      </Container>
      {!isDisabled && allowDeleteSelection ? (
        <ButtonContainer>
          <Button onClick={callbackSetStateShowModalDelete}>
            <FormattedMessage {...messages.deleteSelected} />
          </Button>
        </ButtonContainer>
      ) : null}
    </section>
  );
}

export default ImageManager;
